import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './DetailPage.css';
import DetailPageHero from './DetailPageHero';
import Footer from './Footer';
import InfoTag from './InfoTag';
import { useZoomTransition, beginTransition, endTransition } from '../ZoomTransition';
import { preventOrphan } from '../typography';

/** If duration is a year range (e.g. "2024 – 2025"), return "1 Year" or "2 Years". Otherwise return as-is. */
function formatDurationDisplay(duration) {
  if (!duration || typeof duration !== 'string') return duration;
  const match = duration.match(/^(\d{4})\s*[-–]\s*(\d{4})$/);
  if (!match) return duration;
  const start = parseInt(match[1], 10);
  const end = parseInt(match[2], 10);
  const years = Math.max(0, end - start);
  return years === 1 ? '1 Year' : `${years} Years`;
}

const DetailPage = ({
  title,
  heroSrc,
  content,
  year,
  intent,
  duration,
  role,
  children
}) => {
  const durationDisplay = duration ? formatDurationDisplay(duration) : null;
  const navigate = useNavigate();
  const { offset } = useZoomTransition();

  const viewportRef = useRef(null);

  const handleBack = () => {
    beginTransition();
    // Direct visits have no history entry to go back to, so fall back to the home route.
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  return (
    <motion.div
      ref={viewportRef}
      className="detail-page-viewport"
      initial={{ opacity: 0, x: -offset.x, y: offset.y, scale: 0.571, filter: 'blur(8px)' }}
      animate={{ opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)' }}
      exit={{ opacity: 0, x: -offset.x, y: offset.y, scale: 0.571, filter: 'blur(10px)' }}
      transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        opacity: { duration: 0.4 },
        filter: { duration: 0.4 }
      }}
      onAnimationComplete={() => {
        // Clear the finished filter: it otherwise leaves a stacking context that
        // knocks out the sticky bar's backdrop-filter.
        if (viewportRef.current) viewportRef.current.style.filter = '';
        endTransition();
      }}
    >
      <div className="sticky-bar">
        <div className="detail-page-container">
          <button type="button" className="back-button" onClick={handleBack}>
            <svg width="12" height="16" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.41 10.59L2.83 6L7.41 1.41L6 0L0 6L6 12L7.41 10.59Z" fill="currentColor"/>
            </svg>
            Back
          </button>
        </div>
      </div>

      <div className="detail-page">
        <div className="detail-page-container">
          <DetailPageHero src={heroSrc} lazy={false} adaptiveAspectRatio={false}/>

          <div className="content">
            <p className='detail-page-title'>{preventOrphan(title)}</p>
            {(year || intent || durationDisplay || role) && (
              <div className="info-tags-container">
                {year && <InfoTag headline="Year" subtitle={year} />}
                {durationDisplay && <InfoTag headline="Duration" subtitle={durationDisplay} />}
                {intent && <InfoTag headline="Intent" subtitle={intent} />}
                {role && <InfoTag headline="Role" subtitle={role} />}
              </div>
            )}
            <p>{content}</p>
          </div>

          {children}

          <Footer/>
        </div>
      </div>
    </motion.div>
  );
};

export default DetailPage;
